import {Box, createStyles, Group, Select, Stack, Text} from '@mantine/core';
import React, {useMemo, useState} from 'react';

import {AntibodyFormat} from 'antibodies/AntibodyTypedefs';
import {getComponentColumnLabelByRef} from 'antibodies/nested-data-creation/full-screen/ProteinCreationTableUtil';
import {generateSpreadsheetComponentColumns} from 'antibodies/nested-data-creation/spreadsheet-import/AntibodyNestedDataCreationSpreadsheetImportUtil';
import {NdcSpreadsheetComponentFormatType} from 'antibodies/nested-data-creation/spreadsheet-import/constants/AntibodyNestedDataCreationSpreadsheetImportConstants';
import Antibody from 'antibodies/visualization/Antibody';

const useStyles = createStyles((theme) => ({
  glyphContainer: {
    flex: 1,
    padding: theme.spacing.xl,
    borderRadius: theme.radius.md,
    backgroundColor: theme.colors.gray[0],
  },
  selectionPanel: {
    flex: 1,
  },
}));

interface ProteinCreationComponentSelectionPaneProps {
  format: AntibodyFormat;
  startingType: NdcSpreadsheetComponentFormatType;
  selectedLabel: string | null;
  onSelectLabel: (label: string | null) => void;
}

/**
 * Picks a component column by clicking the antibody glyph or choosing from a list.
 */
export default function ProteinCreationComponentSelectionPane({
  format,
  startingType,
  selectedLabel,
  onSelectLabel,
}: ProteinCreationComponentSelectionPaneProps): JSX.Element {
  const {classes} = useStyles();
  const [hoveredRef, setHoveredRef] = useState<number | null>(null);

  const columnLabels = useMemo(
    () => generateSpreadsheetComponentColumns(format, startingType).map(({label}) => label),
    [format, startingType]
  );
  const labelByRef = useMemo(
    () => getComponentColumnLabelByRef(format, startingType),
    [format, startingType]
  );
  const [selectedRef = null, ...additionalSelectedRefs] = [...labelByRef]
    .filter(([, label]) => label === selectedLabel)
    .map(([ref]) => ref);

  return (
    <Group align="stretch" noWrap={true} spacing="lg">
      <Box className={classes.glyphContainer}>
        <Antibody
          format={format}
          showLabels={true}
          showEmptyOptionalDomains={true}
          interactivity={{
            hoveredRef,
            selectedRef,
            additionalSelectedRefs,
            setHoveredRef,
            setSelectedRef: (ref) => {
              onSelectLabel(ref == null ? null : (labelByRef.get(ref) ?? null));
            },
            proteinIdsByRef: {},
            inaccessibleProteinIdsByRef: {},
          }}
        />
      </Box>
      <Stack spacing="md" className={classes.selectionPanel}>
        <Text size="sm">Select the protein component to add sequences to.</Text>
        <Select
          label="Component"
          placeholder="Select a component"
          data={columnLabels}
          value={selectedLabel}
          onChange={onSelectLabel}
        />
      </Stack>
    </Group>
  );
}
